const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  // Get token from localStorage
  getToken() {
    return localStorage.getItem('authToken');
  }

  // Set token in localStorage
  setToken(token) {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  // Get refresh token from localStorage
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // Set refresh token in localStorage
  setRefreshToken(token) {
    if (token) {
      localStorage.setItem('refreshToken', token);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  // Clear all tokens
  clearTokens() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }

  // Legacy method for compatibility
  clearRefreshToken() {
    this.clearTokens();
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add Authorization header if token exists
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await fetch(`${this.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error('Refresh failed');
    }

    const data = await response.json();
    // Store new tokens
    this.setToken(data.token);
    if (data.refreshToken) {
      this.setRefreshToken(data.refreshToken);
    }
    return data.token;
  }

  async request(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - try to refresh token (but not for auth endpoints or retries)
        if (response.status === 401 && !isRetry && !endpoint.startsWith('/auth/')) {
          try {
            // Prevent multiple refresh calls
            if (!this.isRefreshing) {
              this.isRefreshing = true;
              this.refreshPromise = this.refreshAccessToken();
            }

            await this.refreshPromise;
            this.isRefreshing = false;

            // Retry the original request with new token
            return this.request(endpoint, options, true);
          } catch (refreshError) {
            this.isRefreshing = false;
            // Refresh failed - clear tokens and redirect to login
            this.clearTokens();
            window.location.href = '/login';
            throw refreshError;
          }
        }

        const error = new Error(data.message || 'Request failed');
        error.response = { status: response.status, data };
        throw error;
      }

      return { data, status: response.status };
    } catch (error) {
      if (!error.response) {
        error.response = { status: 500, data: { message: 'Network error' } };
      }
      throw error;
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Logout from all devices
  async revokeAllTokens() {
    const response = await this.post('/auth/revoke-tokens', {});
    this.clearTokens();
    return response;
  }
}

const api = new ApiService();
export default api;